import { Box, Slider } from "@mui/material";
import { memo, useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { IconButton } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import { ContrastTwoTone, LightModeTwoTone, ExpandLess, ExpandMore, Tune } from "@mui/icons-material";
import DisplayText from "../../components/DisplayText/DisplayText";

function useResizeObserver<T extends HTMLElement>() {
    const ref = useRef<T | null>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });

    const updateSize = useCallback((entry: ResizeObserverEntry) => {
        const { contentRect } = entry;
        setSize({
            width: contentRect.width,
            height: contentRect.height,
        });
    }, []);

    useLayoutEffect(() => {
        if (!ref.current) return;

        const observer = new ResizeObserver(([entry]) => {
            updateSize(entry);
        });

        observer.observe(ref.current);
        return () => {
            observer.disconnect();
        };
    }, [updateSize]);

    return [ref, size] as const;
}

interface IImageMediaViewerProps {
    url: string;
    isFullscreen: boolean;
}

const ImageMediaViewer: React.FC<IImageMediaViewerProps> = ({ url, isFullscreen }) => {
    const [containerRef, { width: containerWidth, height: containerHeight }] = useResizeObserver<HTMLDivElement>();
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [image, setImage] = useState<HTMLImageElement | null>(null);
    const [imageNaturalWidth, setImageNaturalWidth] = useState<number>(0);
    const [imageNaturalHeight, setImageNaturalHeight] = useState<number>(0);
    const [brightness, setBrightness] = useState(0);
    const [contrast, setContrast] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isZoomed, setIsZoomed] = useState(false);

    const imageScale = useMemo(() => {
        if (containerWidth === 0 || containerHeight === 0 || imageNaturalWidth === 0 || imageNaturalHeight === 0)
            return 0;

        return Math.min(containerWidth / imageNaturalWidth, containerHeight / imageNaturalHeight);
    }, [containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight, isFullscreen]);

    useEffect(() => {
        const image = new Image();
        image.crossOrigin = "use-credentials";
        image.src = url;
        image.onload = () => {
            setImageNaturalWidth(image.naturalWidth);
            setImageNaturalHeight(image.naturalHeight);
            setImage(image);
        };
    }, [url]);

    useEffect(() => {
        if (!image || !canvasRef.current) return;
        if (image && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            if (!ctx) {
                return;
            }

            canvas.width = image.width;
            canvas.height = image.height;
            canvas.style.width = `${image.width}px`;
            canvas.style.height = `${image.height}px`;

            ctx.drawImage(image, 0, 0);
        }
    }, [image, imageScale]);

    return (
        <Box sx={{ height: "100%", width: "100%", position: "relative" }} ref={containerRef}>
            {imageScale > 0 && (
                <TransformWrapper
                    key={`${containerWidth}x${containerHeight}`}
                    initialScale={imageScale}
                    minScale={imageScale}
                    maxScale={imageScale * 100}
                    centerOnInit
                    onZoomStart={() => {}}
                    onZoom={(e) => {
                        setIsZoomed(true);
                    }}
                    panning={{ disabled: !isZoomed }}
                >
                    {({ resetTransform }) => (
                        <>
                            <IconButton
                                onClick={() => {
                                    resetTransform();
                                    setIsZoomed(false);
                                }}
                                sx={{
                                    "position": "absolute",
                                    "top": 8,
                                    "right": 8,
                                    "backgroundColor": "rgba(0, 0, 0, 0.5)",
                                    "color": "white",
                                    "padding": "4px 12px",
                                    "borderRadius": "20px",
                                    "fontSize": "0.875rem",
                                    "display": "flex",
                                    "gap": 1,
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                                    },
                                    "zIndex": 1,
                                }}
                            >
                                {isZoomed ? <RestartAltIcon fontSize="small" /> : <ZoomInIcon fontSize="small" />}
                                {isZoomed ? "Reset zoom" : "Scroll to zoom in"}
                            </IconButton>
                            <TransformComponent
                                wrapperStyle={{
                                    width: "100%",
                                    height: "100%",
                                }}
                            >
                                <Box sx={{ width: "100%", height: "100%" }}>
                                    <canvas
                                        ref={canvasRef}
                                        style={{
                                            width: "100%",
                                            height: "100%",
                                            filter: `brightness(${brightness + 1}) contrast(${contrast + 1})`,
                                        }}
                                    />
                                </Box>
                            </TransformComponent>
                        </>
                    )}
                </TransformWrapper>
            )}
            <Box
                sx={{
                    "position": "absolute",
                    "top": 8,
                    "left": 8,
                    "padding": "4px 12px",
                    "borderRadius": "20px",
                    "backgroundColor": "rgba(0, 0, 0, 0.5)",
                    "color": "white",
                    "fontSize": "0.875rem",
                    "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                    },
                    "zIndex": 1,
                }}
            >
                <Box
                    sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Tune fontSize="small" />
                        <DisplayText style={{ marginLeft: 4 }} text="Controls" type="bodySmall" />
                    </Box>
                    <IconButton
                        sx={{
                            "color": "white",
                            "padding": "0px 4px",
                            "fontSize": "0.875rem",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                            },
                        }}
                    >
                        {isMenuOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </Box>
                {isMenuOpen && (
                    <Box sx={{ minWidth: 200, marginTop: 12 }}>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        width: "70%",
                                        gap: 5,
                                        alignItems: "center",
                                    }}
                                >
                                    <LightModeTwoTone fontSize="small" sx={{ fontSize: "0.8rem" }} />
                                    <DisplayText text="Brightness:" type="bodyXSmall" />
                                    <DisplayText
                                        text={brightness.toFixed(1)}
                                        type="bodyXSmall"
                                        variant={brightness === 0 ? "regular" : "semiBold"}
                                    />
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setBrightness(0);
                                    }}
                                    sx={{
                                        "backgroundColor": "transparent",
                                        "color": "#26a9e0",
                                        "padding": 2,
                                        "fontSize": "0.875rem",
                                        "fontWeight": "bold",
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                    size="small"
                                    disabled={brightness === 0}
                                >
                                    Reset
                                </IconButton>
                            </Box>
                            <Slider
                                value={brightness}
                                size="small"
                                onChange={(e, value) => setBrightness(value as number)}
                                min={-1}
                                max={1}
                                step={0.1}
                                sx={{ color: "#26a9e0" }}
                            />
                        </Box>
                        <Box sx={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "flex-start",
                                        width: "70%",
                                        gap: 5,
                                        alignItems: "center",
                                    }}
                                >
                                    <ContrastTwoTone fontSize="small" sx={{ fontSize: "0.8rem" }} />
                                    <DisplayText text="Contrast:" type="bodyXSmall" />
                                    <DisplayText
                                        text={contrast.toFixed(1)}
                                        type="bodyXSmall"
                                        variant={contrast === 0 ? "regular" : "semiBold"}
                                    />
                                </Box>
                                <IconButton
                                    onClick={() => {
                                        setContrast(0);
                                    }}
                                    sx={{
                                        "backgroundColor": "transparent",
                                        "color": "#26a9e0",
                                        "padding": 2,
                                        "fontSize": "0.875rem",
                                        "fontWeight": "bold",
                                        "&:hover": {
                                            backgroundColor: "transparent",
                                        },
                                    }}
                                    size="small"
                                    disabled={contrast === 0}
                                >
                                    Reset
                                </IconButton>
                            </Box>
                            <Slider
                                value={contrast}
                                onChange={(e, value) => setContrast(value as number)}
                                min={-1}
                                max={1}
                                step={0.1}
                                sx={{ color: "#26a9e0" }}
                                size="small"
                            />
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default memo(ImageMediaViewer);
